import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import Details from './Details';
import { useModal } from '../../../../components/Modals/ModalWrapperHooks';

import { SelectedBorrowAsset } from '@/features/Modals/ui/BorrowModal';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import {
    AssetIcon,
    Button,
    ButtonVariant,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/shared/ui';

export const ConfirmationTransactionModal = () => {
    const { modalProps } = useModal();

    const { selectedAssets, tokenSymbol } = modalProps || {};

    const dispatch = useDispatch();

    const getTermsAndConditions = () => {
        return (
            <p
                className='bg-content-lightAccent rounded text-content-accent text-md font-medium mt-[40px] mb-5 px-5 py-4 
                    [&_a]:text-content-accent [&_a]:font-semibold [&_a]:no-underline [&_a:hover]:underline'
            >
                By confirming this transaction, you agree to the{' '}
                <Link to={'/terms-and-conditions'} target='_blank'>
                    Terms of Use
                </Link>
            </p>
        );
    };

    const onSubmit = () => {
        const pendingLabel =
            selectedAssets && selectedAssets.length > 1
                ? `${selectedAssets.length} pending actions`
                : `${selectedAssets.length} pending action`;

        dispatch(
            showModalAction({
                modalName: ModalName.REGISTER_MARGIN_ACCOUNT_MODAL,
                modalTitle: 'Confirm Transaction',
                modalProps: {
                    ...modalProps,
                    description: pendingLabel,
                    border: false,
                },
            }),
        );
        return;
    };

    const getAssets = () => {
        return selectedAssets?.map((asset: SelectedBorrowAsset, index: number) => {
            return (
                <AssetIcon
                    key={index}
                    assetName={asset.symbol}
                    className='!w-[28px] !h-[28px] !p-[0px]'
                />
            );
        });
    };

    const getAssetNames = () => {
        if (selectedAssets.length > 2) {
            return (
                <>
                    <p className='text-[16px] font-semibold text-content-primary mr-2'>
                        {selectedAssets[0].symbol}
                    </p>
                    <span className='mr-2'>/</span>
                    <p className='text-[16px] font-semibold text-content-primary mr-2'>
                        +{selectedAssets.length - 1} more
                    </p>
                </>
            );
        }

        return selectedAssets?.map((asset: SelectedBorrowAsset, index: number) => {
            return (
                <>
                    <p
                        key={index}
                        className='text-[16px] font-semibold text-content-primary
                        mr-2'
                    >
                        {asset.symbol}
                    </p>
                    {index < selectedAssets.length - 1 && <span className='mr-2'>/</span>}
                </>
            );
        });
    };

    const getTooltipText = () => {
        return (
            <>
                {selectedAssets?.map((asset: SelectedBorrowAsset, index: number) => {
                    return (
                        <p key={index}>
                            {asset.symbol} - {asset.amount}
                        </p>
                    );
                })}
            </>
        );
    };

    return (
        <div className='w-[500px] pt-3'>
            <div className='flex justify-between items-center mb-5'>
                <p className='font-medium text-[14px] text-content-fourth m-0'>Supply Collateral</p>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className='flex items-center'>
                                <div
                                    className={`flex justify-end 
                                        ${selectedAssets.length > 1 ? '[&_img]:-ml-3' : ''} 
                                        [&_img]:bg-background-primary [&_img]:p-1 [&_img]:rounded-full`}
                                >
                                    {getAssets()}
                                </div>

                                <div className='flex items-center ml-2'>{getAssetNames()}</div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>{getTooltipText()}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
            <div className='flex justify-between items-center mb-5'>
                <p className='font-medium text-[14px] text-content-fourth m-0'>Borrow</p>
                <div className='flex items-center'>
                    <div className='flex justify-end [&_img]:-ml-3 [&_img]:bg-background-primary [&_img]:p-1 [&_img]:rounded-full'>
                        <AssetIcon
                            assetName={tokenSymbol}
                            className='!w-[28px] !h-[28px] !p-[0px] !m-0'
                        />
                    </div>
                    <div className='flex items-center ml-2'>
                        <p className='text-[16px] font-semibold text-content-primary mr-2'>
                            {tokenSymbol}
                        </p>
                    </div>
                </div>
            </div>
            <Details />
            {getTermsAndConditions()}
            <div className='w-[100%]'>
                <Button variant={ButtonVariant.Primary} onClick={onSubmit} className='w-[100%]'>
                    Confirm Transaction
                </Button>
            </div>
        </div>
    );
};
